import { ConsoleLogger } from '@proposify/frontend-observability';
import type { ReactNode } from 'react';
import type React from 'react';
import { createContext, useState, useContext, useCallback, useRef, useEffect } from 'react';
import type { Editor } from '@tiptap/react';

export interface MultiEditorContextProps {
  editors: Map<string, Editor>;
  registerEditor: (id: string, editor: Editor) => void;
  unregisterEditor: (id: string) => void;
  getAllEditors: () => Editor[];
  getEditor: (id: string) => Editor | undefined;
  executeOnAllEditors: (command: (editor: Editor) => void) => void;
  findEditorWithThread: (threadId: string) => Editor | undefined;
  executeWhenEditorsReady: (command: (editors: Editor[]) => boolean | void, timeout?: number) => void;
  executeWhenEditorReady: (id: string, command: (editor: Editor) => void, timeout?: number) => void;
}

interface MultiEditorProviderProps {
  children: ReactNode;
}

export const MultiEditorContext = createContext<MultiEditorContextProps>({} as MultiEditorContextProps);

export const MultiEditorProvider: React.FC<MultiEditorProviderProps> = ({ children }) => {
  const [editors, setEditors] = useState<Map<string, Editor>>(new Map());
  const editorsRef = useRef<Map<string, Editor>>(editors);
  const pendingTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const pendingEditorRetriesRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    editorsRef.current = editors;
  }, [editors]);

  useEffect(() => {
    return () => {
      pendingTimeoutsRef.current.forEach((id) => clearTimeout(id));
      pendingTimeoutsRef.current.clear();
      pendingEditorRetriesRef.current.forEach((id) => clearTimeout(id));
      pendingEditorRetriesRef.current.clear();
    };
  }, []);

  const registerEditor = useCallback((id: string, editor: Editor) => {
    setEditors((prev) => {
      const newMap = new Map(prev);
      newMap.set(id, editor);
      return newMap;
    });
  }, []);

  const unregisterEditor = useCallback((id: string) => {
    setEditors((prev) => {
      const newMap = new Map(prev);
      newMap.delete(id);
      return newMap;
    });
  }, []);

  const getAllEditors = useCallback(() => {
    return Array.from(editors.values());
  }, [editors]);

  const getEditor = useCallback(
    (id: string) => {
      return editors.get(id);
    },
    [editors]
  );

  const executeOnAllEditors = useCallback(
    (command: (editor: Editor) => void) => {
      editors.forEach((editor) => {
        command(editor);
      });
    },
    [editors]
  );

  const findEditorWithThread = useCallback(
    (threadId: string) => {
      for (const editor of editors.values()) {
        if (editor.commands?.hasThread(threadId)) {
          return editor;
        }
      }
      return undefined;
    },
    [editors]
  );

  const executeWhenEditorsReady = useCallback((command: (editors: Editor[]) => boolean | void, timeout = 5000) => {
    const startTime = Date.now();

    const tryExecute = () => {
      const currentEditors = Array.from(editorsRef.current.values());

      if (currentEditors.length > 0) {
        const didComplete = command(currentEditors);
        if (didComplete !== false) {
          return;
        }
      }

      if (Date.now() - startTime < timeout) {
        const timeoutId = setTimeout(() => {
          pendingTimeoutsRef.current.delete(timeoutId);
          tryExecute();
        }, 500);
        pendingTimeoutsRef.current.add(timeoutId);
      } else {
        ConsoleLogger.warn(
          currentEditors.length > 0 ? 'Timeout waiting for editor condition to be met' : 'Timeout waiting for editors to be ready',
          {
            editorCount: currentEditors.length,
            elapsedMs: Date.now() - startTime,
          }
        );
      }
    };

    tryExecute();
  }, []);

  const executeWhenEditorReady = useCallback((id: string, command: (editor: Editor) => void, timeout = 3000) => {
    const existing = pendingEditorRetriesRef.current.get(id);
    if (existing !== undefined) clearTimeout(existing);

    const startTime = Date.now();

    const tryExecute = () => {
      const editor = editorsRef.current.get(id);
      if (editor) {
        pendingEditorRetriesRef.current.delete(id);
        command(editor);
        return;
      }

      if (Date.now() - startTime < timeout) {
        pendingEditorRetriesRef.current.set(id, setTimeout(tryExecute, 50));
      } else {
        pendingEditorRetriesRef.current.delete(id);
        console.warn('[MultiEditorContext] executeWhenEditorReady timed out waiting for', id);
      }
    };

    tryExecute();
  }, []);

  const context: MultiEditorContextProps = {
    editors,
    registerEditor,
    unregisterEditor,
    getAllEditors,
    getEditor,
    executeOnAllEditors,
    findEditorWithThread,
    executeWhenEditorsReady,
    executeWhenEditorReady,
  };

  return <MultiEditorContext.Provider value={context}>{children}</MultiEditorContext.Provider>;
};

export const useMultiEditor = (): MultiEditorContextProps => {
  const context = useContext(MultiEditorContext);

  if (!context) {
    throw new Error('useMultiEditor must be used within a MultiEditorProvider');
  }

  return context;
};
